import React from 'react';
import styled from 'styled-components';
import { FaInstagram, FaWhatsapp, FaMailBulk } from 'react-icons/fa';
import { Link } from 'react-scroll';
import { useNavigate } from 'react-router-dom';

const FooterSection = styled.footer`
  background: var(--color-brand-deep);
  color: var(--color-white);
  padding: 4.5rem 0 2rem;

  @media screen and (max-width: 768px) {
    padding-bottom: 5rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 2rem;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const FooterAbout = styled.div``;

const FooterLogo = styled.div`
  width: 150px;
  height: 45px;
  margin-bottom: 1.5rem;
  background-image: url('/images/Logo-white.png');
  background-size: contain;
  background-position: center left;
  background-repeat: no-repeat;
`;

const FooterDescription = styled.p`
  font-family: var(--font-body);
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 320px;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialIcon = styled.a`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    color: var(--color-white);
    background: var(--color-accent);
    transform: translateY(-3px);
  }
`;

const FooterLinks = styled.div``;

const FooterTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 1.15rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const LinksList = styled.ul`
  list-style: none;
`;

const LinkItem = styled.li`
  margin-bottom: 0.8rem;
`;

const linkStyle = `
  font-family: var(--font-body);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    color: var(--color-accent);
  }
`;

const FooterLink = styled(Link)`
  ${linkStyle}
`;

const ExternalLink = styled.a`
  ${linkStyle}
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
  margin: 3rem 0 2rem;
`;

const Copyright = styled.div`
  text-align: center;
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.55);
`;

const Footer = () => {
  const router = useNavigate();

  return (
    <FooterSection>
      <Container>
        <FooterGrid>
          <FooterAbout>
            <FooterLogo/>
            <FooterDescription>
              Transforme a gestão do seu restaurante com nossa solução completa e intuitiva. Controle total do seu negócio na palma da sua mão.
            </FooterDescription>
            <SocialIcons>
              <SocialIcon href="https://instagram.com/sofchef_brasil" target="_blank" aria-label="Instagram">
                <FaInstagram />
              </SocialIcon>
              <SocialIcon href="https://wa.me/551632036800?text=Olá,%20gostaria%20de%20saber%20mais%20sobre%20o%20SofChef!" target="_blank" aria-label="Whatsapp">
                <FaWhatsapp />
              </SocialIcon>
              <SocialIcon href="mailto:sofchef.sistemas@gmail.com" target="_blank" aria-label="Email">
                <FaMailBulk />
              </SocialIcon>
            </SocialIcons>
          </FooterAbout>

          <FooterLinks>
            <FooterTitle>Navegação</FooterTitle>
            <LinksList>
              <LinkItem>
                <FooterLink to="home" smooth={true} duration={500} offset={-80} onClick={() => router('/#home')}>
                  Início
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="features" smooth={true} duration={500} offset={-80} onClick={() => router('/#features')}>
                  Recursos
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="pricing" smooth={true} duration={500} offset={-80} onClick={() => router('/#pricing')}>
                  Preços
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink onClick={() => router('/downloads')}>
                  Downloads
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="about" smooth={true} duration={500} offset={-80} onClick={() => router('/#about')}>
                  Sobre
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="about" smooth={true} duration={500} offset={-80} onClick={() => window.location.replace('https://ajuda.sofchef.com.br')}>
                  Ajuda
                </FooterLink>
              </LinkItem>
            </LinksList>
          </FooterLinks>

          <FooterLinks>
            <FooterTitle>Recursos</FooterTitle>
            <LinksList>
              <LinkItem>
                <ExternalLink href="https://app.sofchef.com.br" target="_blank">
                  Entrar na plataforma
                </ExternalLink>
              </LinkItem>
              <LinkItem>
                <ExternalLink href="https://wa.me/551632036800?text=Olá,%20preciso%20de%20suporte%20técnico%20para%20o%20meu%20sistema%20SofChef." target="_blank">
                  Suporte técnico
                </ExternalLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="about" smooth={true} duration={500} offset={-80} onClick={() => router('/privacy-policies')}>
                  Políticas de privacidade
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="about" smooth={true} duration={500} offset={-80} onClick={() => router('/terms-of-use')}>
                  Termos de uso
                </FooterLink>
              </LinkItem>
            </LinksList>
          </FooterLinks>
        </FooterGrid>

        <Divider />

        <Copyright>
          &copy; {new Date().getFullYear()} SofChef. Todos os direitos reservados.
        </Copyright>
      </Container>
    </FooterSection>
  );
};

export default Footer;
